import { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Brain as BrainIcon,
  Send,
  Plus,
  Trash2,
  Lock,
  Eye,
  EyeOff,
  ImagePlus,
  Loader2,
  KeyRound,
  MessageSquare,
  LogOut,
  X,
  Copy,
  ArrowLeft,
  Globe,
  HardHat,
  Mic,
  MicOff,
  Volume2,
  VolumeX,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "acorn_admin_token";

function authHeader(token) {
  return { headers: { Authorization: `Bearer ${token}` } };
}

// ===== VOICE HELPERS (browser-native, free) =====
function speakText(text) {
  if (!text || typeof window === "undefined" || !window.speechSynthesis) return;
  try {
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 1.0;
    u.pitch = 1.0;
    u.volume = 1.0;
    const voices = window.speechSynthesis.getVoices();
    const preferred = voices.find((v) => /Google US English|Samantha|en-US/i.test(v.name + v.lang));
    if (preferred) u.voice = preferred;
    window.speechSynthesis.speak(u);
  } catch {
    /* ignore */
  }
}

function stopSpeaking() {
  if (typeof window !== "undefined" && window.speechSynthesis) {
    window.speechSynthesis.cancel();
  }
}

function getSpeechRecognition() {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

// ===== LOGIN =====
function BrainLogin({ onSuccess }) {
  const [pw, setPw] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!pw) return toast.error("Enter your password");
    setBusy(true);
    try {
      await axios.get(`${API}/brain/sessions`, authHeader(pw));
      localStorage.setItem(TOKEN_KEY, pw);
      toast.success("Brain online 🧠");
      onSuccess(pw);
    } catch (err) {
      toast.error(err?.response?.status === 401 ? "Wrong password" : "Could not connect");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone flex items-center justify-center p-6 font-body">
      <form onSubmit={submit} className="card-brutal p-8 w-full max-w-md space-y-5" data-testid="brain-login">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 bg-safety border-2 border-forest flex items-center justify-center">
            <BrainIcon className="w-7 h-7 text-bone" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display text-3xl tracking-tight leading-none">Acorn² Brain</div>
            <div className="text-xs uppercase tracking-[0.25em] text-safety font-bold mt-1">Owner Only</div>
          </div>
        </div>
        <p className="text-sm text-forest/80">
          Your private AI helper — ask anything, drop a photo of a tree for a quick read, save passwords safely.
        </p>
        <label className="label-brutal" htmlFor="brain-pw">Password</label>
        <div className="relative">
          <input
            id="brain-pw"
            data-testid="brain-login-password"
            type={show ? "text" : "password"}
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            className="input-brutal pr-12"
            autoFocus
          />
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-forest/60"
          >
            {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
        <button
          type="submit"
          data-testid="brain-login-submit"
          disabled={busy}
          className="btn-primary w-full py-4 text-base flex items-center justify-center gap-2"
        >
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Lock className="w-5 h-5" />}
          UNLOCK BRAIN
        </button>
        <a href="/admin" className="block text-center text-xs uppercase tracking-widest text-forest/60 hover:text-safety font-bold pt-2">
          ← Back to Admin
        </a>
      </form>
    </div>
  );
}

// ===== CHAT BUBBLE =====
function ChatBubble({ msg }) {
  const isUser = msg.role === "user";
  const editedImg = msg.edited_image_base64
    ? `data:${msg.edited_image_mime || "image/png"};base64,${msg.edited_image_base64}`
    : null;
  const [speaking, setSpeaking] = useState(false);

  const handleSpeak = () => {
    if (speaking) {
      stopSpeaking();
      setSpeaking(false);
      return;
    }
    speakText(msg.text || "");
    setSpeaking(true);
    // SpeechSynthesis doesn't always fire 'end' reliably across browsers;
    // approximate by polling
    const t = setInterval(() => {
      if (!window.speechSynthesis?.speaking) {
        setSpeaking(false);
        clearInterval(t);
      }
    }, 400);
  };

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} mb-3`} data-testid={`msg-${msg.role}`}>
      <div
        className={`max-w-[85%] p-4 border-2 border-forest text-sm whitespace-pre-wrap leading-relaxed ${
          isUser ? "bg-safety text-bone" : "bg-bone"
        }`}
      >
        {(msg.attachments || []).filter((a) => a.kind === "image").map((a, i) => (
          <div key={i} className="mb-2 -m-1">
            <div className="text-[10px] uppercase tracking-widest font-bold opacity-70 mb-1">📷 Photo attached</div>
          </div>
        ))}
        {msg.text}
        {editedImg && (
          <div className="mt-3 border-2 border-forest">
            <img src={editedImg} alt="Edited" className="block w-full" data-testid="edited-image" />
            <div className="bg-forest text-bone text-[10px] uppercase tracking-widest font-bold p-2 flex items-center justify-between">
              <span>✨ Edited Photo</span>
              <a
                href={editedImg}
                download={`acorn-edit-${msg.id}.png`}
                className="text-safety hover:underline"
              >
                Download
              </a>
            </div>
          </div>
        )}
        {!isUser && msg.text && (
          <button
            onClick={handleSpeak}
            className="mt-2 text-[10px] uppercase tracking-widest font-bold text-safety hover:text-forest flex items-center gap-1"
            data-testid="speak-message-btn"
          >
            {speaking ? (
              <><VolumeX className="w-3 h-3" /> Stop reading</>
            ) : (
              <><Volume2 className="w-3 h-3" /> Read aloud</>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

// ===== CHAT VIEW =====
function ChatView({ token, sessionId, onSessionCreated }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [images, setImages] = useState([]); // {data_base64, preview}
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [webSearch, setWebSearch] = useState(false);
  const [listening, setListening] = useState(false);
  const [handsFree, setHandsFree] = useState(false);
  const recogRef = useRef(null);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);

  const loadMessages = useCallback(async () => {
    if (!sessionId) {
      setMessages([]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/brain/sessions/${sessionId}/messages`, authHeader(token));
      setMessages(data);
    } catch {
      toast.error("Couldn't load messages");
    } finally {
      setLoading(false);
    }
  }, [sessionId, token]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const onFiles = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((f) => {
      if (!f.type.startsWith("image/")) return;
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result;
        const base64 = String(dataUrl).split(",")[1];
        setImages((prev) => [...prev, { data_base64: base64, mime_type: f.type, preview: dataUrl }]);
      };
      reader.readAsDataURL(f);
    });
    e.target.value = "";
  };

  const send = async () => {
    if (!text.trim() && images.length === 0) return;
    if (editMode && images.length === 0) {
      toast.error("Attach a photo to edit");
      return;
    }
    if (editMode && !text.trim()) {
      toast.error("Tell me what to change in the photo");
      return;
    }
    setSending(true);
    const userText = text.trim() || "(Photo)";
    const localUser = {
      id: `local-${Date.now()}`,
      role: "user",
      text: editMode ? `✨ Edit: ${userText}` : userText,
      attachments: images.map((i) => ({ kind: "image", mime_type: i.mime_type })),
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, localUser]);
    const snapshotImages = images;
    const wasEdit = editMode;
    setText("");
    setImages([]);
    setEditMode(false);
    try {
      if (wasEdit) {
        const { data } = await axios.post(
          `${API}/brain/edit-image`,
          {
            instruction: userText,
            images: snapshotImages.map((i) => ({
              kind: "image",
              mime_type: i.mime_type,
              data_base64: i.data_base64,
            })),
          },
          authHeader(token),
        );
        setMessages((prev) => [
          ...prev,
          {
            id: `edit-${Date.now()}`,
            role: "assistant",
            text: data.text || "Here's the edited photo. Save it and show your customer 📸",
            edited_image_base64: data.image_base64,
            edited_image_mime: data.mime_type,
            created_at: new Date().toISOString(),
          },
        ]);
      } else {
        const { data } = await axios.post(
          `${API}/brain/chat`,
          {
            session_id: sessionId,
            text: userText,
            web_search: webSearch,
            attachments: snapshotImages.map((i) => ({
              kind: "image",
              mime_type: i.mime_type,
              data_base64: i.data_base64,
            })),
          },
          authHeader(token),
        );
        setMessages((prev) => [...prev, data]);
        if (handsFree && data?.text) {
          // Hands-free mode → auto read assistant reply
          setTimeout(() => speakText(data.text), 200);
        }
        if (!sessionId) onSessionCreated?.(data.session_id);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Brain is sleeping — try again");
      setMessages((prev) => prev.filter((m) => m.id !== localUser.id));
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // ===== VOICE INPUT =====
  const toggleMic = () => {
    const Rec = getSpeechRecognition();
    if (!Rec) {
      toast.error("Voice typing isn't supported in this browser. Use Chrome.");
      return;
    }
    if (listening) {
      try { recogRef.current?.stop(); } catch { /* ignore */ }
      setListening(false);
      return;
    }
    const rec = new Rec();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = "en-US";
    let finalSoFar = "";
    rec.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalSoFar += t + " ";
        else interim += t;
      }
      setText((prev) => {
        // Replace the last interim portion with new text
        return (finalSoFar + interim).trim();
      });
    };
    rec.onerror = (e) => {
      if (e.error === "not-allowed") {
        toast.error("Mic permission denied. Allow microphone in your browser settings.");
      } else if (e.error !== "no-speech" && e.error !== "aborted") {
        toast.error(`Mic error: ${e.error}`);
      }
      setListening(false);
    };
    rec.onend = () => {
      setListening(false);
      // Auto-speak hint
    };
    try {
      rec.start();
      recogRef.current = rec;
      setListening(true);
      toast.success("Listening... tap mic again to stop");
    } catch {
      toast.error("Could not start microphone");
    }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 sm:p-6" data-testid="brain-chat-scroll">
        {loading && (
          <div className="flex justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-safety" />
          </div>
        )}
        {!loading && messages.length === 0 && (
          <div className="text-center py-12 text-forest/70">
            <BrainIcon className="w-14 h-14 mx-auto mb-4 text-safety" strokeWidth={1.5} />
            <div className="font-display text-2xl tracking-tight mb-2">What&apos;s up, boss?</div>
            <p className="text-sm max-w-md mx-auto mb-4">
              Ask me anything — tree ID from a photo, write a customer reply, price a job, marketing ideas, you name it.
            </p>
            <div className="inline-block border-2 border-forest bg-bone px-4 py-3 text-left max-w-md space-y-2">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-safety mb-1">✨ NEW: Edit Photos</div>
                <p className="text-xs text-forest/80">
                  Attach a tree photo, tap <strong>✨ EDIT</strong>, and say things like &ldquo;show this tree trimmed&rdquo; or &ldquo;remove this tree from the yard&rdquo; — Brain returns the edited image. Show your customer the AFTER. 🎯
                </p>
              </div>
              <div className="pt-2 border-t border-forest/15">
                <div className="text-[10px] uppercase tracking-widest font-bold text-safety mb-1">🎤 NEW: Talk To Brain</div>
                <p className="text-xs text-forest/80">
                  Tap the <strong>🎤 mic</strong> button to talk instead of type. Tap the <strong>🔊 speaker</strong> button to make Brain read replies out loud. Great for using up in a tree or driving!
                </p>
              </div>
              <div className="pt-2 border-t border-forest/15">
                <div className="text-[10px] uppercase tracking-widest font-bold text-safety mb-1">🌐 NEW: Web Search</div>
                <p className="text-xs text-forest/80">
                  Tap the <strong>🌐 globe</strong> button to give Brain LIVE internet access — ask about weather, tree species, current prices, news, anything!
                </p>
              </div>
            </div>
          </div>
        )}
        {messages.map((m) => <ChatBubble key={m.id} msg={m} />)}
        {sending && (
          <div className="flex justify-start mb-3">
            <div className="bg-bone border-2 border-forest p-4 flex items-center gap-2 text-sm text-forest/70">
              <Loader2 className="w-4 h-4 animate-spin text-safety" />{" "}
              {editMode
                ? "Editing your photo... ~10 sec"
                : webSearch
                ? "Searching the web... 🌐"
                : "Thinking..."}
            </div>
          </div>
        )}
      </div>

      {images.length > 0 && (
        <div className="border-t-2 border-forest bg-bone px-4 py-2 flex items-center gap-2 overflow-x-auto">
          {images.map((img, i) => (
            <div key={i} className="relative flex-shrink-0">
              <img src={img.preview} alt="" className="h-16 w-16 object-cover border-2 border-forest" />
              <button
                onClick={() => setImages((prev) => prev.filter((_, j) => j !== i))}
                className="absolute -top-2 -right-2 bg-safety text-bone w-5 h-5 border-2 border-forest flex items-center justify-center"
              >
                <X className="w-3 h-3" strokeWidth={3} />
              </button>
            </div>
          ))}
          <div className="flex gap-1 ml-auto flex-shrink-0">
            <button
              type="button"
              onClick={() => setEditMode(false)}
              data-testid="mode-ask"
              className={`px-3 py-2 border-2 border-forest text-xs uppercase tracking-widest font-bold ${
                !editMode ? "bg-forest text-bone" : "bg-bone text-forest"
              }`}
            >
              💬 Ask
            </button>
            <button
              type="button"
              onClick={() => {
                if (images.length > 4) {
                  toast.error("Max 4 photos at once");
                  return;
                }
                setEditMode(true);
              }}
              data-testid="mode-edit"
              className={`px-3 py-2 border-2 border-forest text-xs uppercase tracking-widest font-bold ${
                editMode ? "bg-safety text-bone" : "bg-bone text-forest"
              }`}
            >
              ✨ Edit
            </button>
          </div>
        </div>
      )}

      <div className="border-t-2 border-forest bg-white p-3 flex items-end gap-2">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          onChange={onFiles}
          className="hidden"
          data-testid="brain-file-input"
        />
        <button
          onClick={() => fileRef.current?.click()}
          className="btn-dark px-3 py-3 flex-shrink-0"
          style={{ background: "#1B2A22", color: "#F4F1ED" }}
          data-testid="brain-attach-btn"
          title="Attach photo"
        >
          <ImagePlus className="w-5 h-5" />
        </button>
        <button
          type="button"
          onClick={() => setWebSearch((s) => !s)}
          className="btn-dark px-3 py-3 flex-shrink-0 border-2"
          style={{
            background: webSearch ? "#E65C00" : "#1B2A22",
            color: "#F4F1ED",
            borderColor: "#1B2A22",
          }}
          data-testid="brain-search-toggle"
          title={webSearch ? "Web search ON (tap to turn off)" : "Tap to enable web search"}
        >
          <Globe className="w-5 h-5" />
        </button>
        <button
          type="button"
          onClick={toggleMic}
          className={`btn-dark px-3 py-3 flex-shrink-0 border-2 ${listening ? "animate-pulse" : ""}`}
          style={{
            background: listening ? "#E65C00" : "#1B2A22",
            color: "#F4F1ED",
            borderColor: "#1B2A22",
          }}
          data-testid="brain-mic-btn"
          title={listening ? "Tap to stop listening" : "Tap to talk (voice typing)"}
        >
          {listening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
        </button>
        <button
          type="button"
          onClick={() => {
            setHandsFree((s) => {
              const next = !s;
              if (next) toast.success("Hands-free ON — Brain will talk back");
              else { stopSpeaking(); toast("Hands-free OFF"); }
              return next;
            });
          }}
          className="btn-dark px-3 py-3 flex-shrink-0 border-2"
          style={{
            background: handsFree ? "#E65C00" : "#1B2A22",
            color: "#F4F1ED",
            borderColor: "#1B2A22",
          }}
          data-testid="brain-handsfree-btn"
          title={handsFree ? "Hands-free ON (auto-read replies)" : "Turn on hands-free mode"}
        >
          {handsFree ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
        </button>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKey}
          placeholder={
            editMode
              ? "What should I change? e.g. 'remove the dead limbs and show after-trim view'"
              : webSearch
              ? "🌐 Web search ON — ask me anything current (weather, prices, news)..."
              : "Ask anything, or attach a tree photo..."
          }
          className={`input-brutal flex-1 resize-none py-2 min-h-[48px] max-h-[140px] ${
            editMode || webSearch ? "ring-2 ring-safety" : ""
          }`}
          rows={1}
          data-testid="brain-input"
        />
        <button
          onClick={send}
          disabled={sending || (!text.trim() && images.length === 0)}
          className="btn-primary px-4 py-3 flex-shrink-0"
          data-testid="brain-send-btn"
        >
          {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" strokeWidth={2.5} />}
        </button>
      </div>
    </div>
  );
}

// ===== VAULT =====
function VaultView({ token }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | entry
  const [reveal, setReveal] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/brain/vault`, authHeader(token));
      setEntries(data);
    } catch {
      toast.error("Couldn't load vault");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const copy = (v, label) => {
    navigator.clipboard.writeText(v);
    toast.success(`${label} copied`);
  };

  const remove = async (id) => {
    if (!confirm("Delete this saved password?")) return;
    try {
      await axios.delete(`${API}/brain/vault/${id}`, authHeader(token));
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-bone">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-display text-3xl tracking-tight flex items-center gap-2">
              <KeyRound className="w-7 h-7 text-safety" /> Password Vault
            </div>
            <p className="text-xs uppercase tracking-[0.25em] text-forest/60 mt-1">Encrypted · Owner only</p>
          </div>
          <button
            onClick={() => setEditing("new")}
            className="btn-primary px-4 py-3 flex items-center gap-2 text-sm"
            data-testid="vault-add-btn"
          >
            <Plus className="w-4 h-4" strokeWidth={3} /> ADD
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-safety" />
          </div>
        ) : entries.length === 0 ? (
          <div className="card-brutal p-8 text-center text-forest/70">
            <KeyRound className="w-12 h-12 mx-auto mb-3 text-safety" strokeWidth={1.5} />
            <div className="font-display text-xl mb-1">No saved passwords yet</div>
            <p className="text-sm">Tap ADD to save your first one. Stored encrypted in your private database.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {entries.map((e) => (
              <div key={e.id} className="card-brutal p-4" data-testid={`vault-entry-${e.id}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <div className="font-display text-lg tracking-tight">{e.label}</div>
                    {e.url && <div className="text-xs text-forest/60 truncate">{e.url}</div>}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button
                      onClick={() => setEditing(e)}
                      className="text-xs uppercase tracking-widest font-bold text-forest/70 hover:text-safety px-2"
                    >
                      Edit
                    </button>
                    <button onClick={() => remove(e.id)} className="text-safety hover:text-forest p-1">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {e.username && (
                  <div className="flex items-center justify-between text-sm py-1 border-t border-forest/15">
                    <span className="text-forest/60 text-xs uppercase tracking-widest">User</span>
                    <button
                      onClick={() => copy(e.username, "Username")}
                      className="font-mono text-forest hover:text-safety flex items-center gap-2"
                    >
                      {e.username} <Copy className="w-3 h-3" />
                    </button>
                  </div>
                )}
                <div className="flex items-center justify-between text-sm py-1 border-t border-forest/15">
                  <span className="text-forest/60 text-xs uppercase tracking-widest">Pwd</span>
                  <div className="flex items-center gap-3">
                    <span className="font-mono">
                      {reveal[e.id] ? e.password : "•".repeat(Math.min(12, e.password.length))}
                    </span>
                    <button
                      onClick={() => setReveal((r) => ({ ...r, [e.id]: !r[e.id] }))}
                      className="text-forest/60 hover:text-safety"
                    >
                      {reveal[e.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button onClick={() => copy(e.password, "Password")} className="text-forest/60 hover:text-safety">
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {e.note && <div className="text-xs text-forest/70 mt-2 pt-2 border-t border-forest/15">{e.note}</div>}
              </div>
            ))}
          </div>
        )}

        {editing && (
          <VaultEditor
            token={token}
            entry={editing === "new" ? null : editing}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              load();
            }}
          />
        )}
      </div>
    </div>
  );
}

function VaultEditor({ token, entry, onClose, onSaved }) {
  const [label, setLabel] = useState(entry?.label || "");
  const [username, setUsername] = useState(entry?.username || "");
  const [password, setPassword] = useState(entry?.password || "");
  const [url, setUrl] = useState(entry?.url || "");
  const [note, setNote] = useState(entry?.note || "");
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (!label.trim() || !password.trim()) {
      toast.error("Label and password are required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        label: label.trim(),
        username: username.trim() || null,
        password,
        url: url.trim() || null,
        note: note.trim() || null,
      };
      if (entry) {
        await axios.patch(`${API}/brain/vault/${entry.id}`, payload, authHeader(token));
      } else {
        await axios.post(`${API}/brain/vault`, payload, authHeader(token));
      }
      toast.success("Saved");
      onSaved?.();
    } catch {
      toast.error("Could not save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={onClose}>
      <form
        onSubmit={save}
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-lg bg-bone border-2 border-forest hard-shadow-lg max-h-[95vh] overflow-y-auto"
        data-testid="vault-editor"
      >
        <div className="bg-forest text-bone p-4 flex items-center justify-between">
          <div className="font-display text-2xl tracking-tight flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-safety" /> {entry ? "Edit" : "Add"} Password
          </div>
          <button type="button" onClick={onClose}>
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="label-brutal">Label *</label>
            <input data-testid="vault-label-input" className="input-brutal" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Gmail, Google Business" autoFocus />
          </div>
          <div>
            <label className="label-brutal">Username / Email</label>
            <input data-testid="vault-username-input" className="input-brutal" value={username} onChange={(e) => setUsername(e.target.value)} />
          </div>
          <div>
            <label className="label-brutal">Password *</label>
            <input data-testid="vault-password-input" className="input-brutal font-mono" type="text" value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          <div>
            <label className="label-brutal">Website (optional)</label>
            <input data-testid="vault-url-input" className="input-brutal" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://..." />
          </div>
          <div>
            <label className="label-brutal">Note (optional)</label>
            <textarea data-testid="vault-note-input" className="input-brutal min-h-[70px]" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-dark flex-1 py-3" style={{ background: "#1B2A22", color: "#F4F1ED" }}>CANCEL</button>
            <button type="submit" disabled={busy} className="btn-primary flex-1 py-3 flex items-center justify-center gap-2" data-testid="vault-save-btn">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "SAVE"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

// ===== JOB PLAN MODAL =====
function JobPlanModal({ token, onClose }) {
  const [projectType, setProjectType] = useState("");
  const [location, setLocation] = useState("");
  const [dimensions, setDimensions] = useState("");
  const [description, setDescription] = useState("");
  const [customer, setCustomer] = useState("");
  const [busy, setBusy] = useState(false);

  const generate = async (e) => {
    e.preventDefault();
    if (!projectType.trim() || !location.trim() || !description.trim()) {
      toast.error("Project type, location, and description are required");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/brain/job-plan`,
        {
          project_type: projectType.trim(),
          location: location.trim(),
          dimensions: dimensions.trim() || null,
          description: description.trim(),
          customer_name: customer.trim() || null,
        },
        authHeader(token),
      );
      toast.success("Plan ready — opening printable view");
      window.open(`/brain/plan/${data.id}`, "_blank");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate plan");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={onClose}>
      <form
        onSubmit={generate}
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-lg bg-bone border-2 border-forest hard-shadow-lg max-h-[95vh] overflow-y-auto"
        data-testid="job-plan-modal"
      >
        <div className="bg-forest text-bone p-4 flex items-center justify-between">
          <div className="font-display text-2xl tracking-tight flex items-center gap-2">
            <HardHat className="w-5 h-5 text-safety" /> Job Planning Sheet
          </div>
          <button type="button" onClick={onClose}><X className="w-6 h-6" /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-forest/70 leading-relaxed">
            Generates a printable, city-permit-ready planning sheet. PDF-able.
          </p>
          <div>
            <label className="label-brutal">Project Type *</label>
            <input
              data-testid="plan-type-input"
              className="input-brutal"
              value={projectType}
              onChange={(e) => setProjectType(e.target.value)}
              placeholder="Deck, Shed, Tree Removal, Lot Clearing..."
              autoFocus
            />
          </div>
          <div>
            <label className="label-brutal">Property Address / Location *</label>
            <input
              data-testid="plan-location-input"
              className="input-brutal"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="123 Oak St, Kingston TN 37763"
            />
          </div>
          <div>
            <label className="label-brutal">Dimensions / Size</label>
            <input
              data-testid="plan-dim-input"
              className="input-brutal"
              value={dimensions}
              onChange={(e) => setDimensions(e.target.value)}
              placeholder="12ft x 16ft, 8ft tall, etc."
            />
          </div>
          <div>
            <label className="label-brutal">Customer Name (optional)</label>
            <input
              data-testid="plan-customer-input"
              className="input-brutal"
              value={customer}
              onChange={(e) => setCustomer(e.target.value)}
            />
          </div>
          <div>
            <label className="label-brutal">Describe The Job *</label>
            <textarea
              data-testid="plan-desc-input"
              className="input-brutal min-h-[120px]"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What you're building, materials you plan to use, anything unique about the site..."
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-dark flex-1 py-3" style={{ background: "#1B2A22", color: "#F4F1ED" }}>CANCEL</button>
            <button type="submit" disabled={busy} className="btn-primary flex-1 py-3 flex items-center justify-center gap-2" data-testid="plan-generate-btn">
              {busy ? <><Loader2 className="w-5 h-5 animate-spin" /> Building...</> : "GENERATE PLAN"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

// ===== MAIN =====
export default function Brain() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [view, setView] = useState("chat"); // chat | vault
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [showSidebar, setShowSidebar] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);

  // Hide the Emergent badge ONLY on this owner-only page so it doesn't cover the chat input.
  // Badge uses inline !important styles so we must set the property directly on the element.
  useEffect(() => {
    let cancelled = false;
    const hide = () => {
      const el = document.getElementById("emergent-badge");
      if (el) {
        el.style.setProperty("display", "none", "important");
      }
    };
    const show = () => {
      const el = document.getElementById("emergent-badge");
      if (el) {
        el.style.setProperty("display", "inline-flex", "important");
      }
    };
    // The badge may be injected async by the platform — poll briefly to catch it
    hide();
    const interval = setInterval(() => {
      if (cancelled) return;
      hide();
    }, 400);
    const stopTimer = setTimeout(() => clearInterval(interval), 5000);
    return () => {
      cancelled = true;
      clearInterval(interval);
      clearTimeout(stopTimer);
      show();
    };
  }, []);

  const loadSessions = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/brain/sessions`, authHeader(token));
      setSessions(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      }
    }
  }, [token]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const newChat = () => {
    setActiveSession(null);
    setShowSidebar(false);
    setView("chat");
  };

  const deleteSession = async (id) => {
    if (!confirm("Delete this chat?")) return;
    try {
      await axios.delete(`${API}/brain/sessions/${id}`, authHeader(token));
      if (activeSession === id) setActiveSession(null);
      loadSessions();
    } catch {
      toast.error("Could not delete");
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  if (!token) return <BrainLogin onSuccess={setToken} />;

  return (
    <div className="h-screen bg-bone font-body flex overflow-hidden">
      {/* Sidebar */}
      <aside
        className={`${
          showSidebar ? "translate-x-0" : "-translate-x-full"
        } md:translate-x-0 fixed md:static inset-y-0 left-0 z-40 w-72 bg-forest text-bone border-r-2 border-forest flex flex-col transition-transform`}
      >
        <div className="p-4 border-b-2 border-bone/20 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-safety border-2 border-bone flex items-center justify-center">
              <BrainIcon className="w-5 h-5" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-display text-lg tracking-tight leading-none">Acorn² Brain</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-safety">Owner Only</div>
            </div>
          </div>
          <button onClick={() => setShowSidebar(false)} className="md:hidden">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-3 space-y-2 border-b-2 border-bone/20">
          <button
            onClick={() => { setView("chat"); newChat(); }}
            className="w-full bg-safety border-2 border-bone py-3 text-bone font-bold text-sm flex items-center justify-center gap-2 hover:translate-y-[-2px] transition-transform"
            data-testid="new-chat-btn"
          >
            <Plus className="w-4 h-4" strokeWidth={3} /> NEW CHAT
          </button>
          <button
            onClick={() => { setView("vault"); setShowSidebar(false); }}
            className={`w-full border-2 border-bone py-3 text-sm font-bold flex items-center justify-center gap-2 ${
              view === "vault" ? "bg-bone text-forest" : "bg-transparent text-bone"
            }`}
            data-testid="vault-tab-btn"
          >
            <KeyRound className="w-4 h-4" strokeWidth={3} /> PASSWORD VAULT
          </button>
          <button
            onClick={() => { setShowPlanModal(true); setShowSidebar(false); }}
            className="w-full border-2 border-bone py-3 text-sm font-bold flex items-center justify-center gap-2 bg-transparent text-bone hover:bg-bone hover:text-forest transition-colors"
            data-testid="plan-tab-btn"
          >
            <HardHat className="w-4 h-4" strokeWidth={3} /> JOB PLAN SHEET
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          <div className="text-[10px] uppercase tracking-[0.25em] text-bone/60 px-2 py-2">Recent Chats</div>
          {sessions.length === 0 && (
            <div className="text-xs text-bone/50 px-2 py-3">No chats yet. Start a new one.</div>
          )}
          {sessions.map((s) => (
            <div
              key={s.id}
              className={`flex items-center group cursor-pointer text-sm border-l-2 transition-colors ${
                activeSession === s.id ? "border-safety bg-bone/10" : "border-transparent hover:bg-bone/5"
              }`}
            >
              <button
                onClick={() => {
                  setActiveSession(s.id);
                  setView("chat");
                  setShowSidebar(false);
                }}
                className="flex-1 text-left px-3 py-2 truncate min-w-0"
                data-testid={`session-${s.id}`}
              >
                <MessageSquare className="w-3 h-3 inline mr-2 text-safety" />
                {s.title || "Untitled"}
              </button>
              <button
                onClick={() => deleteSession(s.id)}
                className="opacity-0 group-hover:opacity-100 px-2 py-2 text-bone/60 hover:text-safety"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>

        <div className="p-3 border-t-2 border-bone/20 space-y-2">
          <a
            href="/admin"
            className="w-full border-2 border-bone py-2 text-xs uppercase tracking-widest font-bold text-bone flex items-center justify-center gap-2 hover:bg-bone hover:text-forest transition-colors"
          >
            <ArrowLeft className="w-3 h-3" /> Admin Dashboard
          </a>
          <button
            onClick={logout}
            className="w-full text-xs uppercase tracking-widest font-bold text-bone/60 hover:text-safety flex items-center justify-center gap-2 py-1"
            data-testid="brain-logout-btn"
          >
            <LogOut className="w-3 h-3" /> Lock Brain
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {showSidebar && (
        <div className="md:hidden fixed inset-0 bg-black/60 z-30" onClick={() => setShowSidebar(false)} />
      )}

      {/* Main */}
      <main className="flex-1 flex flex-col min-w-0">
        <header className="md:hidden bg-forest text-bone p-3 flex items-center justify-between border-b-2 border-forest">
          <button onClick={() => setShowSidebar(true)} className="p-1">
            <MessageSquare className="w-6 h-6" />
          </button>
          <div className="font-display text-lg tracking-tight flex items-center gap-2">
            <BrainIcon className="w-5 h-5 text-safety" /> {view === "chat" ? "Chat" : "Vault"}
          </div>
          <button onClick={newChat} className="p-1">
            <Plus className="w-6 h-6" />
          </button>
        </header>

        {view === "chat" ? (
          <ChatView
            token={token}
            sessionId={activeSession}
            onSessionCreated={(id) => {
              setActiveSession(id);
              loadSessions();
            }}
          />
        ) : (
          <VaultView token={token} />
        )}
      </main>

      {showPlanModal && <JobPlanModal token={token} onClose={() => setShowPlanModal(false)} />}
    </div>
  );
}
